/**
 * WhatsApp Embedded Signup — two-channel completion coordinator (P1).
 *
 * The Embedded Signup flow delivers its result on TWO independent channels
 * that can arrive in ANY order:
 *   1. the authorization CODE — via the `FB.login` callback, and
 *   2. the WA_EMBEDDED_SIGNUP session (phone_number_id / waba_id) — via a
 *      cross-window `postMessage`.
 *
 * Backend completion requires BOTH the code and phone_number_id. This
 * coordinator captures each channel independently, never assumes which
 * arrives first, fires completion EXACTLY ONCE once both are present, and
 * tears down deterministically on completion, cancel, error, timeout or
 * dispose. It is a pure state machine (no DOM / network) so it is unit
 * testable; the component wires the browser events into it.
 */

export const FB_ORIGINS = [
  'https://www.facebook.com',
  'https://web.facebook.com',
  'https://business.facebook.com',
];

export function createSignupFlow({ onComplete, onCancel, onCleanup } = {}) {
  const state = { code: null, phoneNumberId: null, wabaId: null, event: null, finished: false };
  let done = false;      // completion already fired
  let settled = false;   // terminal — cleanup already run

  const FINISH_EVENTS = ['FINISH', 'FINISH_ONLY_WABA', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'];

  const cleanup = () => {
    if (settled) return;
    settled = true;
    onCleanup?.();
  };

  const maybeComplete = () => {
    if (done || settled) return;
    // Complete once we have the CODE and a terminal session FINISH. The
    // phone_number_id may be absent (e.g. Coexistence) — the server discovers
    // the asset authoritatively, so we never block on it here.
    if (state.code && state.finished) {
      done = true;
      cleanup();
      onComplete?.({ code: state.code, phoneNumberId: state.phoneNumberId,
                     wabaId: state.wabaId, event: state.event });
    }
  };

  const cancel = (silent) => {
    if (done || settled) return;
    cleanup();
    onCancel?.({ silent: !!silent });
  };

  return {
    // ── Channel 1: FB.login callback (authorization CODE) ──
    handleAuthResponse(resp) {
      if (done || settled) return;
      const code = resp?.authResponse?.code;
      if (resp?.status === 'connected' && code) {
        state.code = code;
        maybeComplete();            // waits for the session FINISH if not yet here
      } else {
        // No code → genuine cancel / denial; nothing left to wait for.
        cancel(false);
      }
    },

    // ── Channel 2: WA_EMBEDDED_SIGNUP postMessage (session ids + event) ──
    handleMessage(origin, rawData) {
      if (done || settled) return;
      if (!FB_ORIGINS.includes(origin)) return;              // ignore non-Meta origins
      let payload;
      try { payload = JSON.parse(rawData); } catch { return; } // ignore malformed
      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') return;
      const ev = String(payload.event || '').toUpperCase();
      if (payload.data?.phone_number_id) state.phoneNumberId = payload.data.phone_number_id;
      if (payload.data?.waba_id) state.wabaId = payload.data.waba_id;
      if (FINISH_EVENTS.includes(ev)) {
        state.event = ev;
        state.finished = true;
        maybeComplete();            // waits for the code if not yet here
      } else if (ev === 'CANCEL') {
        cancel(false);
      } else if (ev === 'ERROR') {
        cancel(false);              // surfaced failure, not a silent timeout
      }
    },

    // ── Bounded timeout: prevents a dangling listener ──
    handleTimeout() {
      cancel(true);
    },

    // ── Component unmount / caller teardown ──
    dispose() {
      cleanup();
    },

    get isDone() { return done; },
    get isSettled() { return settled; },
  };
}
